/**
 * MiscCostsTable Component
 * 
 * Displays miscellaneous costs (optional and included).
 * Uses normalized data from QuotationFormatter.getMiscCosts()
 * 
 * Columns: Description | Type | Rate | Qty | Amount | GST | Total | Status
 * Footer: Total Included | Total Optional
 * 
 * Usage:
 *   const formatter = new QuotationFormatter(quotation, agency);
 *   const miscData = formatter.getMiscCosts();
 *   
 *   <MiscCostsTable
 *     data={miscData}
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { Receipt } from 'lucide-react';
import { COMPONENT_VARIANTS, formatCurrency } from './displayConfig';

const MiscCostsTable = ({ 
  data = null,
  packageType = 'custom',
  variant = 'detail',
  showTitle = true,
  className = ''
}) => {
  if (!data || !data.costs || data.costs.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;
  const { costs, includedTotal, optionalTotal } = data;
  
  // Separate included and optional costs
  const includedCosts = costs.filter(c => !c.isOptional);
  const optionalCosts = costs.filter(c => c.isOptional);

  // Check if any cost has GST
  const hasGst = costs.some(c => c.gstPercentage > 0);

  return (
    <div 
      className={className}
      data-package-type={packageType}
      data-component="misc-costs"
      data-testid="misc-costs-table"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <Receipt className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Miscellaneous Charges
        </h3>
      )}
      
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className={`w-full ${styles.fontSize}`}>
          <thead>
            <tr className={`${styles.headerBg} ${styles.headerText}`}>
              <th className={`${styles.padding} text-left font-semibold`}>Description</th>
              <th className={`${styles.padding} text-center font-semibold w-24`}>Type</th>
              <th className={`${styles.padding} text-right font-semibold w-24`}>Rate</th>
              <th className={`${styles.padding} text-center font-semibold w-16`}>Qty</th>
              <th className={`${styles.padding} text-right font-semibold w-24`}>Amount</th>
              {hasGst && (
                <th className={`${styles.padding} text-right font-semibold w-28`}>GST</th>
              )}
              <th className={`${styles.padding} text-right font-semibold w-28`}>Total</th>
              <th className={`${styles.padding} text-center font-semibold w-24`}>Status</th>
            </tr>
          </thead>
          <tbody>
            {/* Included Costs */}
            {includedCosts.map((cost, idx) => (
              <tr 
                key={`inc-${idx}`}
                className={idx % 2 === 0 ? 'bg-white' : (variant === 'preview' ? 'bg-slate-50' : 'bg-gray-50')}
              >
                <td className={`${styles.padding} border-t`}>{cost.remarks}</td>
                <td className={`${styles.padding} border-t text-center text-gray-600 text-xs`}>
                  {cost.costType}
                </td>
                <td className={`${styles.padding} border-t text-right`}>
                  {cost.rate}
                </td>
                <td className={`${styles.padding} border-t text-center`}>
                  {cost.quantity || '-'}
                </td>
                <td className={`${styles.padding} border-t text-right`}>{cost.amount}</td>
                {hasGst && (
                  <td className={`${styles.padding} border-t text-right text-gray-500`}>
                    {cost.gstPercentage > 0 ? `${cost.gstPercentage}% (${cost.gstAmount})` : '-'}
                  </td>
                )}
                <td className={`${styles.padding} border-t text-right font-medium`}>
                  {cost.total}
                </td>
                <td className={`${styles.padding} border-t text-center`}>
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-[color-mix(in_srgb,var(--ok)_10%,white)] text-[var(--ok)] text-xs font-medium">
                    Included
                  </span>
                </td>
              </tr>
            ))}
            
            {/* Included Subtotal */}
            {includedCosts.length > 0 && (
              <tr className="bg-[color-mix(in_srgb,var(--navy)_5%,white)] border-t-2 border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
                <td colSpan={hasGst ? 6 : 5} className={`${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  Total Included Charges
                </td>
                <td className={`${styles.padding} text-right font-bold text-[var(--navy)]`}>
                  {includedTotal}
                </td>
                <td className={`${styles.padding}`}></td>
              </tr>
            )}
            
            {/* Optional Costs */}
            {optionalCosts.length > 0 && (
              <>
                <tr className="bg-[color-mix(in_srgb,var(--cyan)_10%,white)]">
                  <td colSpan={hasGst ? 8 : 7} className={`${styles.padding} font-medium text-[var(--navy)]`}>
                    Optional Add-ons (Not included in package total)
                  </td>
                </tr>
                {optionalCosts.map((cost, idx) => (
                  <tr 
                    key={`opt-${idx}`}
                    className="bg-[color-mix(in_srgb,var(--cyan)_5%,white)]"
                  >
                    <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)]`}>{cost.remarks}</td>
                    <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] text-center text-gray-600 text-xs`}>
                      {cost.costType}
                    </td>
                    <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] text-right`}>
                      {cost.rate}
                    </td>
                    <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] text-center`}>
                      {cost.quantity || '-'}
                    </td>
                    <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] text-right`}>{cost.amount}</td>
                    {hasGst && (
                      <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] text-right text-gray-500`}>
                        {cost.gstPercentage > 0 ? `${cost.gstPercentage}% (${cost.gstAmount})` : '-'}
                      </td>
                    )}
                    <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] text-right font-medium`}>
                      {cost.total}
                    </td>
                    <td className={`${styles.padding} border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] text-center`}>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-[color-mix(in_srgb,var(--cyan)_15%,white)] text-[var(--navy)] text-xs font-medium">
                        Optional
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="bg-[color-mix(in_srgb,var(--cyan)_10%,white)] border-t border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
                  <td colSpan={hasGst ? 6 : 5} className={`${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                    Total Optional Charges
                  </td>
                  <td className={`${styles.padding} text-right font-bold text-[var(--navy)]`}>
                    {optionalTotal}
                  </td>
                  <td className={`${styles.padding}`}></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MiscCostsTable;
